import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d8fl71f4ba2002eslqpg';

const enquiryOptions = [
  '1:1 Coaching',
  'Group Coaching',
  'HOME',
  'Retreats',
  'Women Who Heal Conference',
  'The Collection',
  'Speaking',
  'Press',
  'Other',
];

export default function ContactFormSection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    enquiry: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const params = new URLSearchParams();
      params.append('fullName', formData.fullName);
      params.append('email', formData.email);
      params.append('enquiry', formData.enquiry);
      params.append('message', formData.message);

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ fullName: '', email: '', enquiry: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="bg-[#1A1A1A] py-16 md:py-28 lg:py-32">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-3xl mx-auto">
        {/* Eyebrow */}
        <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-4 text-center">
          Other enquiries
        </p>

        {/* Headline */}
        <h2 className="font-heading text-xl md:text-3xl lg:text-5xl text-[#FDFAF8] text-center mb-10 leading-tight">
          Get in touch.
        </h2>

        {/* Contact Form */}
        <form
          id="contact-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Full Name */}
          <div>
            <label htmlFor="fullName" className="block font-body text-sm text-[#E8E8E8] mb-2">
              Full name
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-[#FDFAF8] text-[#1A1A1A] font-body text-sm rounded-md border border-[#C9A84C]/20 focus:outline-none focus:border-[#C9A84C] transition-colors"
              placeholder="Your full name"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block font-body text-sm text-[#E8E8E8] mb-2">
              Email address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-[#FDFAF8] text-[#1A1A1A] font-body text-sm rounded-md border border-[#C9A84C]/20 focus:outline-none focus:border-[#C9A84C] transition-colors"
              placeholder="your@email.com"
            />
          </div>

          {/* Enquiry dropdown */}
          <div>
            <label htmlFor="enquiry" className="block font-body text-sm text-[#E8E8E8] mb-2">
              What you are enquiring about
            </label>
            <select
              id="enquiry"
              name="enquiry"
              value={formData.enquiry}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-[#FDFAF8] text-[#1A1A1A] font-body text-sm rounded-md border border-[#C9A84C]/20 focus:outline-none focus:border-[#C9A84C] transition-colors appearance-none cursor-pointer"
            >
              <option value="" disabled>Select an option</option>
              {enquiryOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block font-body text-sm text-[#E8E8E8] mb-2">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={5}
              maxLength={500}
              className="w-full px-4 py-3 bg-[#FDFAF8] text-[#1A1A1A] font-body text-sm rounded-md border border-[#C9A84C]/20 focus:outline-none focus:border-[#C9A84C] transition-colors resize-none"
              placeholder="Tell me what you are looking for..."
            />
            <p className="text-[#AAAAAA] text-xs font-body mt-1 text-right">
              {formData.message.length}/500
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full inline-flex items-center justify-center px-8 py-4 bg-[#D4006A] text-white text-sm font-body font-medium rounded-full hover:bg-[#B3005A] transition-colors duration-300 disabled:opacity-60"
          >
            {status === 'submitting' ? 'Sending...' : 'Send'}
          </button>

          {/* Status Messages */}
          {status === 'success' && (
            <p className="font-body text-sm text-[#C9A84C] text-center">
              Thank you. Your message has been sent.
            </p>
          )}
          {status === 'error' && (
            <p className="font-body text-sm text-[#D4006A] text-center">
              Something went wrong. Please try again.
            </p>
          )}
        </form>

        {/* Business email */}
        <p className="text-center mt-8">
          <span className="font-body text-sm text-[#AAAAAA]">Or reach me at </span>
          <a
            href="mailto:hello@mandieozee.com"
            className="font-body text-sm text-[#C9A84C] hover:text-[#D4B866] transition-colors"
          >
            hello@mandieozee.com
          </a>
        </p>

        {/* Social links */}
        <div className="flex items-center justify-center gap-5 mt-6">
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 flex items-center justify-center text-[#E8E8E8] hover:text-[#C9A84C] transition-colors"
            aria-label="Instagram"
          >
            <i className="ri-instagram-line text-xl" />
          </a>
          <a
            href="https://linkedin.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 flex items-center justify-center text-[#E8E8E8] hover:text-[#C9A84C] transition-colors"
            aria-label="LinkedIn"
          >
            <i className="ri-linkedin-fill text-xl" />
          </a>
          <a
            href="https://youtube.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 flex items-center justify-center text-[#E8E8E8] hover:text-[#C9A84C] transition-colors"
            aria-label="YouTube"
          >
            <i className="ri-youtube-line text-xl" />
          </a>
        </div>
      </div>
    </section>
  );
}